import {
  type EntitySummary,
  GRAPH_STATUSES,
  isDated,
  itemDateLabel,
  itemMatches,
  itemSpan,
  parseTimelineIds,
  TIMELINE_ITEM_KINDS,
  TIMELINE_KIND_LABELS,
  TIMELINE_MAX_LANES,
  type TimelineFilter,
  type TimelineItem,
  timelineTicks,
  timelineWindow,
} from '@hoopoe/shared';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { CalendarRange, Loader2, Lock, X } from 'lucide-react';
import { useId, useMemo, useState } from 'react';
import { ColorLegend } from '../components/colors/color-by';
import { DataText } from '../components/data/data-text';
import { EntityLink, REVIEW_STATUS_LABELS, StatusBadge } from '../components/entities/entity-link';
import { Section } from '../components/graph/panels';
import { EmptyState } from '../components/guidance/empty-state';
import { EntityAutocomplete } from '../components/guidance/entity-autocomplete';
import { FieldHelp } from '../components/guidance/field-help';
import { Tip } from '../components/guidance/tip';
import { useScreenTour } from '../components/guidance/tour';
import { PageHeader } from '../components/layout/app-shell';
import { ReliabilityBadge } from '../components/sources/reliability-badge';
import { itemLabel, TimelineChart } from '../components/timeline/timeline-chart';
import { Button } from '../components/ui/button';
import { ApiError } from '../lib/api';
import { useStyleOf } from '../lib/colors';
import { useEntitySearch } from '../lib/entities';
import { useSchema } from '../lib/schema';
import { useTimeline } from '../lib/timeline';

// Timeline view (PLAN §9, §12.7 screen 7; task 29): the Events and relation periods of one or more Actors, one
// swimlane per Actor. Dates show at their own precision (a year covers the year, a season its months, "since 2021"
// runs to today); colors follow the active "Color by" mode and outlines the review status, as in every list.
// The lanes live in the URL (?ids=a,b), so a timeline can be shared or opened from a profile.

export interface TimelineSearch {
  /** Lane entity ids, comma-separated, in order. */
  ids?: string;
}

export const Route = createFileRoute('/timeline')({
  validateSearch: (s: Record<string, unknown>): TimelineSearch => {
    const ids = parseTimelineIds(s.ids);
    return ids.length ? { ids: ids.join(',') } : {};
  },
  component: TimelinePage,
});

const TOUR = {
  id: 'timeline',
  steps: [
    {
      target: '[data-tour="timeline-add"]',
      title: 'Add actors',
      body: 'Type a name in any script. Each actor gets its own lane, so you can compare what they did and when.',
      learnMore: 'timeline',
    },
    {
      target: '[data-tour="timeline-filters"]',
      title: 'Narrow it down',
      body: 'Show events, relation periods or both, pick review statuses, and zoom to a range of years.',
    },
    {
      target: '[data-tour="timeline-chart"]',
      title: 'Read the timeline',
      body: 'Diamonds are events, bars are relation periods. Click any item for its dates, roles and where the date came from.',
      learnMore: 'timeline',
    },
  ],
};

const DATE_SOURCE: Record<NonNullable<TimelineItem['dateSource']>, string> = {
  event: 'the event’s own date',
  row: 'the table row',
  claim: 'the relation’s period',
};

const year = (v: string) => (/^\d{4}$/.test(v) ? Number(v) : null);

function TimelinePage() {
  useScreenTour(TOUR);
  const { data: snap } = useSchema();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: '/timeline' });
  const entitySearch = useEntitySearch(snap);
  const styleOf = useStyleOf(snap);
  const ids = useMemo(() => parseTimelineIds(search.ids), [search.ids]);
  const q = useTimeline(ids);
  const [kinds, setKinds] = useState<TimelineItem['kind'][]>([...TIMELINE_ITEM_KINDS]);
  const [statuses, setStatuses] = useState<TimelineFilter['statuses']>([...GRAPH_STATUSES]);
  const [fromYear, setFromYear] = useState('');
  const [toYear, setToYear] = useState('');
  const [selected, setSelected] = useState<string | null>(null);
  const [now] = useState(() => Date.now());
  const uid = { add: useId(), from: useId(), to: useId() };

  const setIds = (next: string[]) => navigate({ search: next.length ? { ids: next.join(',') } : {} });
  const data = ids.length ? q.data : undefined;
  const entities = useMemo(() => {
    const m = new Map<string, EntitySummary>();
    for (const e of data?.entities ?? []) m.set(e.id, e);
    for (const e of data?.lanes ?? []) m.set(e.id, e);
    return m;
  }, [data]);
  const filter: TimelineFilter = useMemo(
    () => ({ kinds, statuses, fromYear: year(fromYear), toYear: year(toYear) }),
    [kinds, statuses, fromYear, toYear],
  );
  const shown = useMemo(() => (data?.items ?? []).filter(i => isDated(i) && itemMatches(i, filter, now)), [data, filter, now]);
  const undated = useMemo(
    () => (data?.items ?? []).filter(i => !isDated(i) && itemMatches(i, { kinds, statuses }, now)),
    [data, kinds, statuses, now],
  );
  const range = useMemo(() => timelineWindow(shown, filter, now), [shown, filter, now]);
  const ticks = useMemo(() => (range ? timelineTicks(range, 10) : []), [range]);
  const legendStyles = useMemo(
    () =>
      [...new Set(shown.map(i => i.entityId).filter((id): id is string => !!id))]
        .map(id => entities.get(id))
        .filter((e): e is EntitySummary => !!e)
        .map(e => styleOf(e)),
    [shown, entities, styleOf],
  );
  const selectedItem = selected ? data?.items.find(i => i.id === selected) : undefined;
  const filtersOn = kinds.length < TIMELINE_ITEM_KINDS.length || (statuses?.length ?? 0) < GRAPH_STATUSES.length || !!fromYear || !!toYear;
  const toggle = <T,>(list: T[], v: T) => (list.includes(v) ? list.filter(x => x !== v) : [...list, v]);

  if (!snap) return <PageHeader title="Timeline" />;

  return (
    <>
      <PageHeader
        title="Timeline"
        description="Events and relation periods for one or more actors, one lane each. Colors, shapes and outlines mean the same as in every list."
      />
      <Tip id="timeline" learnMore="timeline">
        Add a second actor to compare lanes: an event both took part in appears in both. Items without a date are listed under the chart.
      </Tip>

      <div className="mt-4 flex flex-wrap items-end gap-3">
        <div className="min-w-72 flex-1 space-y-1" data-tour="timeline-add">
          <div className="flex items-center gap-1">
            <label htmlFor={uid.add} className="text-xs font-medium">
              Add an actor
            </label>
            <FieldHelp
              label="Add an actor"
              help={`Any name, alias or ID code, in any script. Each actor gets a lane (up to ${TIMELINE_MAX_LANES}).`}
              example="Hassan Haddad, حسن حداد or POL_HADDAD"
              learnMore="timeline"
            />
          </div>
          <EntityAutocomplete
            key={ids.join(',')}
            id={uid.add}
            placeholder={ids.length >= TIMELINE_MAX_LANES ? `At most ${TIMELINE_MAX_LANES} actors` : 'Type a name, alias or ID code'}
            search={async text =>
              ids.length >= TIMELINE_MAX_LANES ? [] : (await entitySearch(['actor'], text)).filter(s => !ids.includes(s.id))
            }
            onSelect={s => setIds([...ids, s.id])}
          />
        </div>
        {ids.length > 0 && (
          <Button size="md" variant="ghost" onClick={() => setIds([])}>
            <X aria-hidden="true" /> Clear
          </Button>
        )}
      </div>

      <fieldset className="mt-3 flex flex-wrap items-end gap-x-6 gap-y-3 rounded-lg border bg-surface p-3" data-tour="timeline-filters">
        <legend className="sr-only">Filters</legend>
        <fieldset className="space-y-1">
          <legend className="block text-xs font-medium">Show</legend>
          <div className="flex flex-wrap gap-3 text-sm">
            {TIMELINE_ITEM_KINDS.map(k => (
              <label key={k} className="flex items-center gap-1.5">
                <input type="checkbox" checked={kinds.includes(k)} onChange={() => setKinds(v => toggle(v, k))} />
                {TIMELINE_KIND_LABELS[k]}
              </label>
            ))}
          </div>
        </fieldset>
        <fieldset className="space-y-1">
          <legend className="block text-xs font-medium">Review status</legend>
          <div className="flex flex-wrap gap-3 text-sm">
            {GRAPH_STATUSES.map(s => (
              <label key={s} className="flex items-center gap-1.5">
                <input type="checkbox" checked={statuses?.includes(s) ?? false} onChange={() => setStatuses(v => toggle(v ?? [], s))} />
                {REVIEW_STATUS_LABELS[s]}
              </label>
            ))}
          </div>
        </fieldset>
        <div className="flex items-end gap-2">
          <div className="space-y-1">
            <label htmlFor={uid.from} className="block text-xs font-medium">
              From year
            </label>
            <input
              id={uid.from}
              inputMode="numeric"
              value={fromYear}
              onChange={e => setFromYear(e.target.value.replace(/\D/g, '').slice(0, 4))}
              placeholder="e.g. 2015"
              className="h-9 w-24 rounded-md border border-input bg-surface px-2 text-sm tabular"
            />
          </div>
          <div className="space-y-1">
            <label htmlFor={uid.to} className="block text-xs font-medium">
              To year
            </label>
            <input
              id={uid.to}
              inputMode="numeric"
              value={toYear}
              onChange={e => setToYear(e.target.value.replace(/\D/g, '').slice(0, 4))}
              placeholder="e.g. 2024"
              className="h-9 w-24 rounded-md border border-input bg-surface px-2 text-sm tabular"
            />
          </div>
          <FieldHelp
            label="Years"
            help="Zoom the timeline to these years. Items that overlap them stay; a date covers its whole span (2020 overlaps June 2020)."
            example="2015 to 2024"
            learnMore="timeline"
          />
        </div>
        {filtersOn && (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              setKinds([...TIMELINE_ITEM_KINDS]);
              setStatuses([...GRAPH_STATUSES]);
              setFromYear('');
              setToYear('');
            }}
          >
            Reset filters
          </Button>
        )}
      </fieldset>

      <div aria-live="polite" className="mt-3 min-h-5 text-sm">
        {q.isFetching ? (
          <span className="flex items-center gap-2 text-muted-foreground">
            <Loader2 className="size-4 animate-spin" aria-hidden="true" /> Loading the timeline…
          </span>
        ) : q.isError ? (
          <span role="alert" className="text-danger-foreground">
            {q.error instanceof ApiError ? q.error.message : 'The timeline could not be loaded. Try again.'}
          </span>
        ) : data?.truncated ? (
          <span className="text-muted-foreground">Some links were left out: there were too many. Remove an actor to see all of them.</span>
        ) : null}
      </div>

      <div data-tour="timeline-chart" className="mt-1">
        {!ids.length ? (
          <EmptyState icon={CalendarRange} title="Start from an actor" learnMore="timeline">
            Type a name above, or open the timeline from any actor’s profile (Connections → Open timeline).
          </EmptyState>
        ) : data && data.lanes.length === 0 ? (
          <EmptyState icon={CalendarRange} title="These actors no longer exist" learnMore="timeline">
            They may have been merged into another entity. Add an actor above.
          </EmptyState>
        ) : data && range ? (
          <TimelineChart
            snap={snap}
            lanes={data.lanes}
            entities={entities}
            items={shown}
            range={range}
            ticks={ticks}
            selected={selected}
            onSelect={id => setSelected(s => (s === id ? null : id))}
            onRemoveLane={id => setIds(ids.filter(x => x !== id))}
            now={now}
          />
        ) : data ? (
          <EmptyState icon={CalendarRange} title="Nothing dated to show" learnMore="timeline">
            {filtersOn
              ? 'No dated item matches the filters. Reset the filters to see everything.'
              : 'These actors have no dated events or relations yet. Add dates on their profiles or on the events.'}
          </EmptyState>
        ) : null}
      </div>

      {data && data.lanes.length > 0 && (
        <div className="mt-4 grid gap-4 lg:grid-cols-[minmax(0,1fr)_20rem]">
          <div className="space-y-4">
            {selectedItem && (
              <ItemDetails
                item={selectedItem}
                entity={selectedItem.entityId ? entities.get(selectedItem.entityId) : undefined}
                lane={entities.get(selectedItem.lane)}
                onClose={() => setSelected(null)}
              />
            )}
            {undated.length > 0 && (
              <Section title={`Without a date (${undated.length})`}>
                <p className="mb-2 text-xs text-muted-foreground">
                  These can’t be placed on the timeline. Add a date on the event, or a period on the relation, to see them above.
                </p>
                <ul aria-label="Items without a date" className="space-y-1 text-sm">
                  {undated.map(i => {
                    const e = i.entityId ? entities.get(i.entityId) : undefined;
                    const lane = entities.get(i.lane);
                    return (
                      <li key={i.id}>
                        <button
                          type="button"
                          aria-pressed={selected === i.id}
                          onClick={() => setSelected(s => (s === i.id ? null : i.id))}
                          className="flex w-full min-w-0 items-center gap-2 rounded px-1 py-0.5 text-start hover:bg-muted aria-pressed:bg-muted"
                          // Named by its visible words (WCAG 2.5.3); the full label (kind, date, status) is the description.
                          title={`${lane?.displayName ?? ''}: ${itemLabel(i, e)}`}
                        >
                          <DataText className="min-w-0 truncate text-muted-foreground">{lane?.displayName}</DataText>
                          <span aria-hidden="true" className="text-muted-foreground">
                            ·
                          </span>
                          <span className="shrink-0 text-muted-foreground">{i.roles[0]}</span>
                          <DataText className="min-w-0 truncate font-medium">{e?.displayName ?? i.text}</DataText>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </Section>
            )}
          </div>
          <Section title="Legend">
            {legendStyles.length > 0 && <ColorLegend styles={legendStyles} />}
            <p className="mt-2 text-xs text-muted-foreground">
              ◆ event · bar = relation period (→ ongoing) · ■ other dated entry. Solid outline = verified, dashed = unreviewed, double with
              ⚠ = disputed · <Lock className="inline size-3" aria-label="lock" /> = confidential. The dashed vertical line is today.
            </p>
          </Section>
        </div>
      )}
    </>
  );
}

function ItemDetails({
  item,
  entity,
  lane,
  onClose,
}: {
  item: TimelineItem;
  entity: EntitySummary | undefined;
  lane: EntitySummary | undefined;
  onClose(): void;
}) {
  const { data: snap } = useSchema();
  const span = itemSpan(item);
  return (
    <Section
      title={item.kind === 'event' ? 'Event' : item.kind === 'period' ? 'Relation' : 'Entry'}
      actions={
        <Button size="sm" variant="ghost" onClick={onClose} aria-label="Close details">
          <X aria-hidden="true" />
        </Button>
      }
    >
      <dl className="grid grid-cols-[8rem_minmax(0,1fr)] gap-x-3 gap-y-1.5 text-sm" data-testid="timeline-details">
        <dt className="text-muted-foreground">{item.kind === 'entry' ? 'Entry' : item.kind === 'event' ? 'Event' : 'Linked to'}</dt>
        <dd className="min-w-0">
          {entity && snap ? <EntityLink schema={snap} entity={entity} /> : <DataText>{item.text ?? '—'}</DataText>}
        </dd>
        <dt className="text-muted-foreground">Actor</dt>
        <dd className="min-w-0">{lane && snap ? <EntityLink schema={snap} entity={lane} /> : '—'}</dd>
        <dt className="text-muted-foreground">{item.kind === 'entry' ? 'Listed in' : 'Role'}</dt>
        <dd>{item.roles.join(', ') || '—'}</dd>
        <dt className="text-muted-foreground">Date</dt>
        <dd>
          {itemDateLabel(item)}
          {item.approximate && ' (approximate)'}
          {item.dateSource && <span className="block text-xs text-muted-foreground">From {DATE_SOURCE[item.dateSource]}</span>}
          {!span && <span className="block text-xs text-muted-foreground">Not on the timeline until it has a date.</span>}
        </dd>
        <dt className="text-muted-foreground">Status</dt>
        <dd className="flex flex-wrap items-center gap-2">
          <StatusBadge status={item.status} />
          {item.sensitivity === 'confidential' && (
            <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
              <Lock className="size-3" aria-hidden="true" /> Confidential
            </span>
          )}
        </dd>
        <dt className="flex items-center gap-1 text-muted-foreground">
          Reliability
          <FieldHelp
            label="Reliability"
            help="The best support any of its claims has: an official document, two independent sources (corroborated), one source, or none yet (hypothesis)."
            learnMore="reliability-status"
          />
        </dt>
        <dd>{item.reliability ? <ReliabilityBadge status={item.reliability} /> : '—'}</dd>
        <dt className="text-muted-foreground">Based on</dt>
        <dd>
          {item.claimIds.length} {item.claimIds.length === 1 ? 'claim' : 'claims'}
        </dd>
      </dl>
      {entity && (
        <div className="mt-3 flex flex-wrap gap-3 text-sm">
          <Link to="/entity/$id" params={{ id: entity.id }} className="font-medium underline-offset-2 hover:underline">
            Open profile
          </Link>
          <Link to="/graph" search={{ focus: entity.id }} className="font-medium underline-offset-2 hover:underline">
            Open in graph explorer
          </Link>
        </div>
      )}
    </Section>
  );
}
